import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import CssBaseline from '@mui/material/CssBaseline';
import { SnackbarProvider } from 'notistack';
import { AuthProvider, useAuth } from './context/AuthContext';
import theme from './theme';
import { API_CONFIG } from './config';
import Layout from './components/Layout';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';

// Directors
import DirectorsList from './pages/directors/DirectorsList';
import AddDirector from './pages/directors/AddDirector';
import EditDirector from './pages/directors/EditDirector';
import DirectorDetails from './pages/directors/DirectorDetails';

// Expiry Alerts
import ExpiryAlertsList from './pages/ExpiryAlertsList';

// Org Structure
import OrganizationalStructure from './pages/OrganizationalStructure';

// Committees
import CommitteesList from './pages/committees/CommitteesList';
import CreateCommittee from './pages/committees/CreateCommittee';
import CommitteeDetails from './pages/committees/CommitteeDetails';

// Companies (GS only)
import CompaniesList from './pages/companies/CompaniesList';
import CreateCompany from './pages/companies/CreateCompany';
import CompanyDetails from './pages/companies/CompanyDetails';

// Company Secretaries (GS only)
import CompanySecretariesList from './pages/company-secretaries/CompanySecretariesList';
import CreateCompanySecretary from './pages/company-secretaries/CreateCompanySecretary';
import CompanySecretaryDetails from './pages/company-secretaries/CompanySecretaryDetails';

// Users & Permissions (GS only)
import UsersPermissions from './pages/UsersPermissions';

// Lookups (GS view, IT Admin edit)
import LookupsAdmin from './pages/lookups/LookupsAdmin';

// Profile
import Profile from './pages/Profile';
import ChangePassword from './pages/ChangePassword';
import PortalGuard from './components/PortalGuard';
import { isGroupScopeRole } from './constants/csAdminRoles';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect to dashboard if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  return !isAuthenticated ? children : <Navigate to="/dashboard" />;
};

/** Group portal only — IT Admin and Group Company Secretary */
const AdminPortalRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!API_CONFIG.IS_ADMIN_PORTAL || !isGroupScopeRole(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AdminPortalLayout = () => (
  <AdminPortalRoute>
    <Outlet />
  </AdminPortalRoute>
);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
        <SnackbarProvider maxSnack={3}>
          <AuthProvider>
            <Router basename={API_CONFIG.PUBLIC_URL || undefined}>
              <PortalGuard>
              <Routes>
                {/* Public Routes */}
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicRoute>
                      <ForgotPassword />
                    </PublicRoute>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/dashboard" />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  
                  {/* Directors Module */}
                  <Route path="directors">
                    <Route index element={<DirectorsList />} />
                    <Route path="add" element={<AddDirector />} />
                    <Route path=":id" element={<DirectorDetails />} />
                    <Route path=":id/edit" element={<EditDirector />} />
                  </Route>

                  {/* Expiry Alerts */}
                  <Route path="expiry-alerts" element={<ExpiryAlertsList />} />

                  {/* Organizational Structure */}
                  <Route path="org-structure" element={<OrganizationalStructure />} />

                  {/* Committees */}
                  <Route path="committees">
                    <Route index element={<CommitteesList />} />
                    <Route path="create" element={<CreateCommittee />} />
                    <Route path=":id/edit" element={<CreateCommittee />} />
                    <Route path=":id" element={<CommitteeDetails />} />
                  </Route>

                  {/* Companies (GS only) */}
                  <Route path="companies">
                    <Route index element={<CompaniesList />} />
                    <Route path="create" element={<CreateCompany />} />
                    <Route path=":id/edit" element={<CreateCompany />} />
                    <Route path=":id" element={<CompanyDetails />} />
                  </Route>

                  {/* Company Secretaries — group portal only */}
                  <Route path="company-secretaries" element={<AdminPortalLayout />}>
                    <Route index element={<CompanySecretariesList />} />
                    <Route path="create" element={<CreateCompanySecretary />} />
                    <Route path=":id/edit" element={<CreateCompanySecretary />} />
                    <Route path=":id" element={<CompanySecretaryDetails />} />
                  </Route>

                  {/* Users & Permissions — group portal only */}
                  <Route
                    path="users-permissions"
                    element={(
                      <AdminPortalRoute>
                        <UsersPermissions />
                      </AdminPortalRoute>
                    )}
                  />

                  {/* Lookups — group portal only */}
                  <Route
                    path="lookups"
                    element={(
                      <AdminPortalRoute>
                        <LookupsAdmin />
                      </AdminPortalRoute>
                    )}
                  />

                  {/* Profile */}
                  <Route path="profile" element={<Profile />} />
                  <Route path="profile/change-password" element={<ChangePassword />} />
                  <Route path="change-password" element={<ChangePassword />} />
                </Route>

                {/* 404 Not Found */}
                <Route path="*" element={<Navigate to="/dashboard" />} />
              </Routes>
              </PortalGuard>
            </Router>
          </AuthProvider>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
